// Login page. Posts username/password to the auth store; on success the store flips
// to "authed" and we redirect to the page the user came from (default "/").
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../lib/auth-store";
import { useThemeStore } from "../lib/theme";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, status, error, loggingIn, clearError } = useAuthStore();
  const { theme, toggle } = useThemeStore();

  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");

  const from = (location.state as { from?: string } | null)?.from ?? "/";

  // Already authed (e.g. session restored) → bounce to the dashboard.
  useEffect(() => {
    if (status === "authed") navigate(from, { replace: true });
  }, [status, from, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const ok = await login(user.trim(), pass);
    if (ok) navigate(from, { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Rocky Monitor
          </h1>
          <button
            type="button"
            onClick={toggle}
            aria-label="Toggle dark mode"
            className="rounded-lg border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {theme === "dark" ? "☀️" : "🌙"}
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            Sign in to view the health &amp; cost dashboard.
          </p>

          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Username
          </label>
          <input
            type="text"
            autoComplete="username"
            value={user}
            onChange={(e) => {
              setUser(e.target.value);
              if (error) clearError();
            }}
            required
            className="mb-4 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />

          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Password
          </label>
          <input
            type="password"
            autoComplete="current-password"
            value={pass}
            onChange={(e) => {
              setPass(e.target.value);
              if (error) clearError();
            }}
            required
            className="mb-4 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800 dark:bg-red-900/40 dark:text-red-300"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loggingIn}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
